/**
 * JAISIU-future: path-specific field overrides for the config form.
 *
 * The schema-driven form (`analyzeConfigSchema` + `renderNode`) renders
 * most settings as native controls. A handful of fields either:
 *   - have a Zod schema the JSON-form generator can't flatten (e.g.
 *     `string | { primary, fallbacks }`), so they degrade to the
 *     "Edit JSON directly" fallback;
 *   - are arrays of records whose item schemas use `.transform()` (which
 *     JSON Schema can't represent), so the schema comes back as an open
 *     `{}` and the analyzer marks them unsupported;
 *   - or are intentionally "power-user" surfaces where the dedicated
 *     panel is the right place to edit (e.g. `agents.list`).
 *
 * For the demo we want all of these to behave like real, saveable
 * settings. Rather than fighting the analyzer, this registry lets each
 * subsystem plug a native control into a specific path. `renderNode`
 * checks the registry before falling back to the JSON editor.
 *
 * Adding an override:
 *   1. Implement a function that returns a Lit TemplateResult given the
 *      standard `FormRenderParams` plus the field-specific schema.
 *   2. Register it under the path where it should apply.
 *   3. Add a node test that round-trips save → reload → re-render.
 */

import type { TemplateResult } from "lit";

export type FormRenderParams = {
  schema: Record<string, unknown>;
  value: unknown;
  path: Array<string | number>;
  hints: Record<string, { label?: string; help?: string }>;
  unsupported: Set<string>;
  disabled: boolean;
  onPatch: (path: Array<string | number>, value: unknown) => void;
};

export type FieldOverride = {
  /** Dotted path matcher (e.g. `p[0]==="channels" && p[1]==="telegram"`). */
  matchPath: (path: Array<string | number>) => boolean;
  /** Stable id for diagnostics & tests. */
  id: string;
};

const registry: FieldOverride[] = [];

/** Register a field override. The most-recently-added override wins on path match. */
export function registerFieldOverride(override: FieldOverride): void {
  registry.push(override);
}

export function resolveFieldOverride(path: Array<string | number>): FieldOverride | null {
  for (let i = registry.length - 1; i >= 0; i--) {
    if (registry[i].matchPath(path)) {
      return registry[i];
    }
  }
  return null;
}

/** Test-only: clear the registry. Production never calls this. */
export function _clearFieldOverridesForTests(): void {
  registry.length = 0;
}

// ---------------------------------------------------------------------------
// Matchers. Each is a small pure function that's easy to unit test.
// ---------------------------------------------------------------------------

export const matchTelegramCapabilities = (p: Array<string | number>) =>
  p.length === 3 && p[0] === "channels" && p[1] === "telegram" && p[2] === "capabilities";

export const matchTelegramCustomCommands = (p: Array<string | number>) =>
  p.length === 3 && p[0] === "channels" && p[1] === "telegram" && p[2] === "customCommands";

export const matchTelegramAccounts = (p: Array<string | number>) =>
  p.length === 3 && p[0] === "channels" && p[1] === "telegram" && p[2] === "accounts";

export const matchGoogleChatServiceAccount = (p: Array<string | number>) =>
  p.length === 3 && p[0] === "channels" && p[1] === "googlechat" && p[2] === "serviceAccount";

export const matchGoogleChatAccounts = (p: Array<string | number>) =>
  p.length === 3 && p[0] === "channels" && p[1] === "googlechat" && p[2] === "accounts";

export const matchAgentsSubagentsModel = (p: Array<string | number>) =>
  p.length === 4 && p[0] === "agents" && p[2] === "subagents" && p[3] === "model";

export const matchAgentsSandboxDockerUlimits = (p: Array<string | number>) =>
  p.length === 5 &&
  p[0] === "agents" &&
  p[2] === "sandbox" &&
  p[3] === "docker" &&
  p[4] === "ulimits";

export const matchAgentsList = (p: Array<string | number>) =>
  p.length === 2 && p[0] === "agents" && p[1] === "list";

export const matchWizard = (p: Array<string | number>) => p.length === 1 && p[0] === "wizard";

// Register all of them. `render` is attached by `attachRenderers` once Lit
// is imported (so node tests don't accidentally pull in Lit).
registerFieldOverride({ id: "telegram.capabilities", matchPath: matchTelegramCapabilities });
registerFieldOverride({ id: "telegram.customCommands", matchPath: matchTelegramCustomCommands });
registerFieldOverride({ id: "telegram.accounts", matchPath: matchTelegramAccounts });
registerFieldOverride({
  id: "googlechat.serviceAccount",
  matchPath: matchGoogleChatServiceAccount,
});
registerFieldOverride({ id: "googlechat.accounts", matchPath: matchGoogleChatAccounts });
registerFieldOverride({
  id: "agents.defaults.subagents.model",
  matchPath: matchAgentsSubagentsModel,
});
registerFieldOverride({
  id: "agents.defaults.sandbox.docker.ulimits",
  matchPath: matchAgentsSandboxDockerUlimits,
});
registerFieldOverride({ id: "agents.list", matchPath: matchAgentsList });
registerFieldOverride({ id: "wizard", matchPath: matchWizard });

// ---------------------------------------------------------------------------
// Round-trip helpers. Pure functions — exported for tests.
// ---------------------------------------------------------------------------

export const TELEGRAM_CAPABILITIES_VALUES = ["off", "dm", "group", "all", "allowlist"] as const;
export type TelegramCapabilitiesScope = (typeof TELEGRAM_CAPABILITIES_VALUES)[number];

export function readTelegramCapabilities(value: unknown): TelegramCapabilitiesScope | null {
  if (Array.isArray(value)) {
    return null;
  }
  if (value && typeof value === "object" && "inlineButtons" in value) {
    const v = (value as { inlineButtons?: unknown }).inlineButtons;
    if (typeof v === "string" && (TELEGRAM_CAPABILITIES_VALUES as readonly string[]).includes(v)) {
      return v as TelegramCapabilitiesScope;
    }
  }
  return null;
}

export function writeTelegramCapabilities(scope: string | null): unknown {
  if (!scope) {
    return [];
  }
  return { inlineButtons: scope };
}

export function readSubagentsModel(value: unknown): {
  primary: string;
  fallbacksRaw: string;
} {
  if (typeof value === "string") {
    return { primary: value, fallbacksRaw: "" };
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const v = value as { primary?: unknown; fallbacks?: unknown };
    const primary = typeof v.primary === "string" ? v.primary : "";
    const fallbacks = Array.isArray(v.fallbacks)
      ? v.fallbacks.filter((f) => typeof f === "string")
      : [];
    return { primary, fallbacksRaw: fallbacks.join(", ") };
  }
  return { primary: "", fallbacksRaw: "" };
}

export function writeSubagentsModel(primary: string, fallbacksRaw: string): unknown {
  const fallbacks = fallbacksRaw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (!primary && fallbacks.length === 0) {
    return undefined;
  }
  if (fallbacks.length === 0) {
    return primary;
  }
  return { primary, fallbacks };
}

export function normalizeUlimitValue(raw: string): number | string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return trimmed;
  }
  // Only treat as a number if it's a pure integer literal (no decimals,
  // no leading zeros, no suffix). Otherwise keep it as a string so the
  // user can write `nofile=1024:2048` or other docker-ompatible forms.
  if (!/^-?\d+$/.test(trimmed)) {
    return trimmed;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : trimmed;
}

// ---------------------------------------------------------------------------
// Renderers. Imported lazily so node tests don't import Lit.
// ---------------------------------------------------------------------------

export type RenderWithLit = (params: FormRenderParams) => TemplateResult | unknown;

let renderersAttached = false;
const rendererRegistry: Map<string, RenderWithLit> = new Map();

export function registerRenderer(id: string, render: RenderWithLit): void {
  rendererRegistry.set(id, render);
}

export function getRenderer(id: string): RenderWithLit | null {
  return rendererRegistry.get(id) ?? null;
}

export function attachRenderers(renderers: Record<string, RenderWithLit>): void {
  for (const [id, render] of Object.entries(renderers)) {
    registerRenderer(id, render);
  }
  renderersAttached = true;
}

export function isRenderersAttached(): boolean {
  return renderersAttached;
}
